import { useTheme } from "@emotion/react";
import { faSpinnerThird } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type AviaryColorProps, colorProfileMapper } from "aviary-tokens";
import type { FC, HTMLProps, Ref } from "react";
import type { ReactI18NextChildren } from "react-i18next";
import { useTranslation } from "react-i18next";

import { l } from "@aviary/locales/i18n";
import type { AviarySize } from "@aviary/types";
import type { LinkProps } from "@shared/react-router-dom";
import { Link } from "@shared/react-router-dom";

import { Skeleton } from "../Skeleton";

import * as styles from "./Button.styles";

type BasicLinkProps = Partial<Pick<LinkProps, "to" | "replace" | "state">>;

export type ButtonAviarySize = Extract<AviarySize, "xsmall" | "small" | "normal" | "large">;

interface ButtonProps<T = HTMLButtonElement>
  extends AviaryColorProps,
    Omit<BasicLinkProps, "innerRef">,
    HTMLProps<T> {
  /**
   * Optionally hide/show the loading icon that overlays the content within the button
   *
   * @default false
   */
  isLoading?: boolean;
  /**
   * Optionally change the button style to only have a border with a transparent background, and colored text
   *
   * @default false
   */
  isOutlined?: boolean;
  /**
   * Optionally change the button style to only be colored text, without any borders or background color
   *
   * @default false
   */
  isText?: boolean;
  innerRef?: Ref<HTMLButtonElement | HTMLAnchorElement>;
  /**
   * Optionally make the button circular
   *
   * @default false
   */
  isCircular?: boolean;
  /**
   * Optionally make the button fullwidth within the container
   *
   * @default false
   */
  isFullWidth?: boolean;
  /**
   * Specify if the button only contains an icon to change the padding/margins
   *
   * @default false
   */
  isIcon?: boolean;
  /**
   * Specify if the button is using the "light" profile, has a white background with dark text. To be used on dark backgrounds only
   *
   * @default false
   */
  isLight?: boolean;
  /**
   * Displays the corresponding Skeleton
   *
   * @default false
   */
  isSkeleton?: boolean;
  /**
   * Specify the size of the button
   *
   */
  isSize?: ButtonAviarySize;
  /**
   * Used with <ButtonGroup> and Split buttons, adds a divider line to visually separate buttons of the same color
   *
   */
  latch?: "left" | "right" | "middle";
  /**
   * Disables the displaying of the loader
   */
  isLoaderDisabled?: boolean;
  /**
   * Button content
   *
   * @default undefined
   */
  children?: ReactI18NextChildren;
  /**
   * @deprecated, use NoStyleButton instead
   */
  noStyle?: boolean;
}

export type ButtonOrLinkType = ButtonProps<HTMLButtonElement | HTMLAnchorElement>;

const Button: FC<ButtonOrLinkType> = ({
  children,
  disabled,
  href,
  innerRef,
  isCircular,
  isColor,
  isFullWidth,
  isIcon,
  isLight,
  isLoading,
  isOutlined,
  isSize,
  isSkeleton,
  isText,
  latch,
  noStyle,
  replace,
  to,
  type,
  isLoaderDisabled,
  ...rest
}: ButtonOrLinkType) => {
  const { t } = useTranslation("aviary");
  const currentTheme = useTheme();
  const themeColors = colorProfileMapper(currentTheme);

  const buttonBuilder = () => {
    if (noStyle) return styles.button.noStyle;

    return [
      isLoading && styles.loadingStyles,
      styles.button.base,
      styles.normalStyles(themeColors[isColor], currentTheme),
      styles.latch(themeColors[isColor], currentTheme)[latch],
      disabled && styles.latch(themeColors[isColor], currentTheme).disabled,
      isFullWidth && styles.button.fullwidth,
      isCircular && styles.circular,
      isText && styles.textTypeStyle(themeColors[isColor]),
      isOutlined && styles.outlinesStyles(themeColors[isColor], currentTheme),
      styles.button[isSize],
      isIcon && iconBuilder(),
      isColor === "system" && systemButtonBuilder(),
      isLight && lightButtonBuilder(),
    ];
  };

  const lightButtonBuilder = () => {
    return (
      !noStyle && [
        styles.lightStyles.base(currentTheme),
        isOutlined && styles.lightStyles.outlined(currentTheme),
        isText && styles.lightStyles.isText(currentTheme),
        isIcon && styles.lightStyles.isIcon(currentTheme),
        isIcon && isText && styles.lightStyles.isText(currentTheme),
        isIcon && isOutlined && styles.lightStyles.outlined(currentTheme),
      ]
    );
  };

  const iconBuilder = () => {
    return (
      !noStyle && [
        isIcon && styles.baseIconStyles,
        isIcon && styles.iconStyles[isSize],
        styles.normalSVG(currentTheme),
        isOutlined && styles.outlineSVG(themeColors[isColor]),
        isText && styles.textTypeSVG(themeColors[isColor]),
      ]
    );
  };

  const systemButtonBuilder = () => {
    return !noStyle && [styles.systemProfileStyles(themeColors.system)];
  };

  const loaderBuilder = () => {
    return (
      !noStyle && [
        styles.loaderIcon,
        styles.normalSVG(currentTheme),
        isOutlined && styles.outlineSVG(themeColors[isColor]),
        isText && styles.textTypeSVG(themeColors[isColor]),
        isLight && styles.lightStyles.isIcon(currentTheme),
      ]
    );
  };

  const renderLoader = () => {
    if (!noStyle && !isLoaderDisabled && isLoading) {
      return (
        <div css={styles.loader}>
          <FontAwesomeIcon
            css={loaderBuilder()}
            icon={faSpinnerThird}
            spin
            title={t(l.aviary.button.Loading)}
          />
        </div>
      );
    }
  };

  const getValidButtonType = () => {
    if (type === "submit" || type === "reset") {
      return type;
    } else {
      return "button";
    }
  };

  const button = () => (
    <button
      css={buttonBuilder()}
      disabled={disabled}
      {...(rest as HTMLProps<HTMLButtonElement>)}
      ref={innerRef as Ref<HTMLButtonElement>}
      type={getValidButtonType()}
    >
      {renderLoader()}
      {children}
    </button>
  );

  const anchor = () => (
    <a
      css={buttonBuilder()}
      role="button"
      href={href}
      {...(rest as HTMLProps<HTMLAnchorElement>)}
      ref={innerRef as Ref<HTMLAnchorElement>}
    >
      {renderLoader()}
      {children}
    </a>
  );

  const routerLink = () => (
    <Link
      css={buttonBuilder()}
      to={to}
      replace={replace}
      data-testid="Link"
      {...rest}
      ref={innerRef as Ref<HTMLAnchorElement>}
    >
      {renderLoader()}
      {children}
    </Link>
  );

  const getElement = () => {
    if (isSkeleton) {
      return <Skeleton type="button" buttonSize={isSize} isFullwidth={isFullWidth} />;
    } else if (!disabled) {
      if (to) {
        return routerLink();
      } else if (href) {
        return anchor();
      }
    }

    return button();
  };

  return getElement();
};

Button.defaultProps = {
  isColor: "primary",
  isSize: "normal",
  isSkeleton: false,
};

export type { ButtonProps };
export { Button };
